import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, InputGroup, Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import HighlightOff from 'mdi-react/HighlightOffIcon';
import Search from 'mdi-react/SearchIcon';

import { defaultGridState } from 'modules/common/constants';
import { MIN_FILTER_VALUE_LENGTH } from 'modules/security/containers/TripsTabContainer/constants';
import { LogicTypes, FilterOperators, DataGrid, DataGridColumn } from 'modules/shared/DataGrid';
import { useGridState } from 'modules/shared/DataGrid/hooks/useGridState';
import { Filter, GridFilter } from 'modules/shared/DataGrid/utils/interfaces';
import SmallSpinner from 'modules/shared/SmallSpinner';
import { RootState } from 'rootReducer';
import { clientDateFormat } from '../../../../entities/Trip';
import { SecurityReportProps } from '../interfaces';
import { getSecurityLoadingReportsRequest } from './actions';
import ReportDepCell from '../cell/ReportIDepCell';
import ReportArrivCell from '../cell/ReportArrivCell';
import { fieldsNames } from './constants';
import classes from './ReportLoading.module.scss';
import ReportToTonn from '../cell/ReportToTonn';
import ReportToPercent from '../cell/ReportToPercent';
import ReportBookingDate from '../cell/ReportBokingDate';

export const ReportLoading: React.FC<SecurityReportProps> = ({ endDate, startDate, onRow, notLoadedMode }) => {
  const dispatch = useDispatch();
  const [filterInputValue, setFilterInputValue] = useState('');

  const filterInputRef = useRef<HTMLInputElement>(null);
  const branchLocal = localStorage.getItem('branchId') || 'null';
  const branchId = JSON.parse(branchLocal)?.id;

  const { reports, loading } = useSelector(
    (state: RootState) => state.securityModuleReducer.reportsReducer.reportLoadingReducer,
  );

  useEffect(() => {
    if (branchId && endDate && startDate && notLoadedMode) {
      dispatch(getSecurityLoadingReportsRequest(`${branchId}`, startDate, endDate, notLoadedMode));
    }
  }, [startDate, endDate, notLoadedMode]);

  const gridState = useGridState(reports, defaultGridState);

  useEffect(() => {
    if (filterInputValue.length < MIN_FILTER_VALUE_LENGTH) {
      gridState.setFilter({
        logic: LogicTypes.and,
        filters: [],
      });
    } else {
      const newFilter = {
        logic: LogicTypes.or,
        filters: fieldsNames.map(
          (name): Filter => ({
            operator: FilterOperators.contains,
            value: filterInputValue,
            field: name,
          }),
        ),
      } as GridFilter;
      gridState.setFilter(newFilter);
    }
  }, [filterInputValue]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterInputValue(e.target.value);
  };

  const handleClearIconClick = () => {
    setFilterInputValue('');
    if (filterInputRef.current) {
      filterInputRef.current.focus();
    }
  };

  return (
    <React.Fragment>
      <Row>
        <div className="w-350 mr-3" />
        <Col className="d-flex align-items-center pb-3 justify-content-end">
          <InputGroup className={classes.filter}>
            <Form.Control
              as="input"
              type="text"
              name="filter"
              onChange={handleFilterChange}
              value={filterInputValue}
              ref={filterInputRef}
            />
            <Search size={18} className={classes.searchIcon} />
            {!!filterInputValue && (
              <HighlightOff size={18} className={classes.clearFilterIcon} onClick={handleClearIconClick} />
            )}
          </InputGroup>
        </Col>
      </Row>
      {loading ? (
        <SmallSpinner />
      ) : (
        <DataGrid
          pageable
          data={gridState.gridData}
          sort={gridState.sort}
          total={gridState.total}
          skip={gridState.pagination?.skip}
          pageSize={gridState.pagination?.pageSize}
          onPageChange={gridState.handlePageChange}
          onSortChange={gridState.handleSortChange}
          onRow={onRow}
          className={classes.tablePrint}
        >
          <DataGridColumn title="Рейс" dataIndex="tripId" key="tripId" width="60px" sortable />
          <DataGridColumn
            title="Дата бронювання"
            dataIndex="bookingDate"
            key="bookingDate"
            render={ReportBookingDate}
            width="10%"
            className={classes.bookingDate}
          />
          <DataGridColumn title="Тип перевізника" dataIndex="сarrierTypeStr" key="driver" width="8%" />
          <DataGridColumn title="Держномер ТЗ" dataIndex="vehicleLicensePlate" key="vehicleLicensePlate" width="5%" />
          <DataGridColumn
            title="Держномер причепа"
            dataIndex="trailerLicensePlate"
            key="trailerLicensePlate"
            width="5%"
          />
          <DataGridColumn title="Водій" dataIndex="driverName" key="driverName" width="5%" />
          <DataGridColumn
            title="Номер посвідчення"
            dataIndex="driverDocumentNumber"
            key="driverDocumentNumber"
            width="5%"
          />
          <DataGridColumn title="Телефон" dataIndex="driverPhone" key="driverPhone" width="5%" />
          <DataGridColumn
            sortable
            title="Дані в'їзду"
            dataIndex="entryDateTime"
            key="entryDateTime"
            dateFormat={clientDateFormat}
            render={ReportArrivCell}
            width="5%"
          />
          <DataGridColumn
            sortable
            title="Дані виїзду"
            dataIndex="departureDateTime"
            key="departureDateTime"
            dateFormat={clientDateFormat}
            render={ReportDepCell}
            width="5%"
          />
          <DataGridColumn
            title="Вага в'їзду, т"
            dataIndex="entryWeight"
            key="entryWeight"
            width="5%"
            render={ReportToTonn}
          />
          <DataGridColumn
            title="Сума по нарядам, НЕТТО, т (SAP)"
            dataIndex="ordersCalculatedNetWeight"
            key="ordersCalculatedNetWeight"
            width="5%"
            render={ReportToTonn}
          />
          <DataGridColumn
            title="Сума по нарядам, БРУТТО, т"
            dataIndex="ordersCalculatedGrossWeight"
            key="ordersCalculatedGrossWeight"
            width="5%"
            render={ReportToTonn}
          />
          <DataGridColumn
            title="Вага перфорації, т"
            dataIndex="perforationWeight"
            key="perforationWeight"
            width="5%"
            render={ReportToTonn}
          />
          <DataGridColumn
            title="Вага виїзду, т"
            dataIndex="departureWeight"
            key="departureWeight"
            width="5%"
            render={ReportToTonn}
          />
          <DataGridColumn
            title="Відхилення, т"
            dataIndex="allowedWeight"
            key="allowedWeight"
            width="5%"
            render={ReportToTonn}
          />
          <DataGridColumn
            title="Відхилення, %"
            dataIndex="weightDeviationInPercent"
            key="weightDeviationInPercent"
            width="5%"
            render={ReportToPercent}
          />
          <DataGridColumn title="Замовник" dataIndex="customer" key="customer" width="5%" />
          <DataGridColumn title="Експедитор" dataIndex="forwarderName" key="forwarderName" width="5%" />
          <DataGridColumn title="Телефон" dataIndex="forwarderPhone" key="forwarderPhone" width="5%" />
          <DataGridColumn title="Коментар охорони" dataIndex="securityMessage" key="securityMessage" width="5%" />
          <DataGridColumn title="Коментар завсклада" dataIndex="directorMessage" key="directorMessage" width="5%" />
          <DataGridColumn title="Номер Заводу" dataIndex="factoryNumber" key="factoryNumber" width="5%" /> {/* new */}
          <DataGridColumn title="Номер Складу" dataIndex="warehouseCode" key="warehouseCode" width="5%" />
          <DataGridColumn
            title="Номер вихідного постачання"
            dataIndex="outputSupplyNumber"
            key="outputSupplyNumber"
            width="5%"
          />
          <DataGridColumn
            title="Кількість позицій у наряді"
            dataIndex="orderItemsCount"
            key="orderItemsCount"
            width="5%"
          />
        </DataGrid>
      )}
    </React.Fragment>
  );
};
